import React, { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAtom, useAtomValue } from 'jotai';
import { get } from 'lodash';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import useCalcTableWidth from '@components/dimension-panel-hz/hooks/useCalcTableWidth';
import NetworkPanel from '@components/top-widget-panel/app-network-panel';
import humanize from '@utils/humanize';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import { metricFilterRulesSelector } from '@store/trends/base/selectors';
import { dimensionTraceId } from './utils/constant';
import { DimensionHeader, DimensionTableContainer } from './styled';
import {
  drillDownFiltersAtom,
  DrilldownItem,
  drillDownListAtom,
  networkDimensionListAtom,
} from './utils/atom';
import Header from './header';
import DimensionTableWidget from './dimensionTable';

function DimensionTables() {
  const [dimensions, setDimensions] = useAtom(networkDimensionListAtom);
  const applyRules = useAtomValue(drillDownFiltersAtom);
  const [drilldownList, setDrilldownList] = useAtom(drillDownListAtom);
  const allDimensionList = useSelector(allDimensionsListSelector);
  /** filter out dimensions not in user dimension list */
  const filteredDimensionList = useMemo(() => {
    const filtered = dimensions.filter((dimension) =>
      allDimensionList.find((it) => it.field === dimension),
    );
    if (filtered.length > 0) {
      return [...filtered, dimensionTraceId];
    }
    return filtered;
  }, [allDimensionList, dimensions]);
  const metricFilter = useSelector(metricFilterRulesSelector);
  const underSecondaryFilter = metricFilter && metricFilter.length > 0;

  const sensors = useSensors(useSensor(PointerSensor));

  function handleDragEnd(event) {
    const { active, over } = event;
    const sourceIdx = filteredDimensionList.indexOf(active.id);
    const destIdx = filteredDimensionList.indexOf(over.id);

    // create new dimension list
    const newDimensionList = Array.from(filteredDimensionList);
    const [reorderedItem] = newDimensionList.splice(sourceIdx, 1);
    newDimensionList.splice(destIdx, 0, reorderedItem);

    setDimensions(newDimensionList);
  }

  const { ref, width } = useCalcTableWidth();

  const onSelect = useCallback(
    (payload) => {
      if (!underSecondaryFilter) {
        return;
      }
      const drilldownRules = payload.map(({ dimension, row }) => {
        const name = get(row, 'name[0]', '');
        const value = get(row, 'value[0]', '');
        return {
          field: dimension,
          key: dimension,
          op: 'eq',
          value,
          display: name,
          originalField: dimension,
        };
      });

      const newRules = drilldownRules.filter((it) => {
        return applyRules.every((rule) => {
          return rule[0].field !== it.field && rule[0].value !== it.value;
        });
      });
      if (newRules.length) {
        const crumbFilters = newRules.map((drilldownRule) => {
          const crumbRules = getRulesFromFilter([[drilldownRule]], true);
          const crumbFilter = getFilterFromRules(crumbRules, true);
          return { name: crumbFilter.name, rule: crumbFilter.rule } as DrilldownItem;
        });
        setDrilldownList([...drilldownList, ...crumbFilters]);
      }
    },
    [applyRules, drilldownList, setDrilldownList, underSecondaryFilter],
  );

  const formatter = useCallback((value, total) => {
    const percent = (value / Number(total)) * 100;
    if (isNaN(percent) || !isFinite(percent)) {
      return 'NA';
    }
    return `${humanize(percent)}%`;
  }, []);

  return (
    <>
      {underSecondaryFilter && (
        <DimensionHeader>
          <div>
            Secondary Filters [SF] - Secondary Filter data is based on top 1k impacted
            sessions/pages
          </div>
          <Header></Header>
        </DimensionHeader>
      )}
      <NetworkPanel enableSSF enableTimeSlice onSelect={onSelect} formatter={formatter} />
      <DimensionTableContainer ref={ref} id="network-dimension-table" width={width}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={filteredDimensionList} strategy={rectSortingStrategy}>
            {filteredDimensionList.map((dimension) => (
              <DimensionTableWidget key={dimension} dimension={dimension} />
            ))}
          </SortableContext>
        </DndContext>
      </DimensionTableContainer>
    </>
  );
}

export default DimensionTables;
